<template>
  <div class="mb-6">
    <div class="flex flex-row items-center mb-1 ml-2">
      <div class="text-lg font-poppins tracking-wide text-dark-primary">
        Expenses
      </div>
      <div class="ml-3 text-xs text-gray-500">
        Live data from the expense system.
      </div>
      <div v-if="errorMsg" class="ml-3 text-xs text-rose-600">
        {{ errorMsg }}
      </div>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1">
      <MetricCard
        title="Awaiting Reimbursement"
        :value="loading ? '—' : formatDollars(awaitingReimbursementTotal)"
        :subtitle="loading ? '' : `${awaitingReimbursementCount} expense${awaitingReimbursementCount === 1 ? '' : 's'}`"
        valueTone="warn"
        borderTone="warn"
        to="/admin-dashboard/expenses"
      />
      <MetricCard
        title="Outstanding Client AR"
        :value="loading ? '—' : formatDollars(arTotal)"
        :subtitle="loading ? '' : `${arCount} expense${arCount === 1 ? '' : 's'}`"
        borderTone="info"
        to="/admin-dashboard/expenses"
      />
      <MetricCard
        title="Expenses This Month"
        :value="loading ? '—' : monthCount"
        :subtitle="loading ? '' : formatDollars(monthTotal)"
        valueTone="default"
        to="/admin-dashboard/expenses"
      />
      <MetricCard
        title="Awaiting Manager Approval"
        :value="loading ? '—' : pendingApprovalCount"
        :subtitle="loading ? '' : formatDollars(pendingApprovalTotal)"
        :badge="pendingApprovalCount > 0 ? 'action needed' : ''"
        :badgeTone="pendingApprovalCount > 0 ? 'warn' : 'neutral'"
        :borderTone="pendingApprovalCount > 0 ? 'warn' : 'default'"
        to="/admin-dashboard/expenses"
      />
      <MetricCard
        title="Drafts In Flight"
        :value="loading ? '—' : draftCount"
        subtitle="Across all submitters"
        to="/admin-dashboard/expenses"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useStore } from 'vuex'
import MetricCard from './MetricCard.vue'
import { getExpenses, getExpenseReport } from '@/services/Expense'

const store = useStore()

const loading = ref(true)
const errorMsg = ref('')

const awaitingReimbursementCount = ref(0)
const awaitingReimbursementTotal = ref(0)
const arCount = ref(0)
const arTotal = ref(0)
const monthCount = ref(0)
const monthTotal = ref(0)
const pendingApprovalCount = ref(0)
const pendingApprovalTotal = ref(0)
const draftCount = ref(0)

function formatDollars(n) {
  const v = Number(n) || 0
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function sumAmounts(records) {
  return records.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0)
}

function firstOfMonthIso() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

async function load() {
  const token = store.state.auth.token
  if (!token) {
    errorMsg.value = 'Not authenticated.'
    loading.value = false
    return
  }
  const monthStart = firstOfMonthIso()
  try {
    const [payroll, ar, monthList, pendingList, draftPage] = await Promise.all([
      getExpenseReport(token, 'payroll').catch((e) => { throw tagError('payroll', e) }),
      getExpenseReport(token, 'ar').catch((e) => { throw tagError('ar', e) }),
      getExpenses(token, `?date_of_expense__gte=${monthStart}&get_all=true`).catch((e) => { throw tagError('month', e) }),
      getExpenses(token, '?status=submitted&get_all=true').catch((e) => { throw tagError('pending', e) }),
      getExpenses(token, '?status=draft&page_size=1').catch((e) => { throw tagError('draft', e) }),
    ])

    awaitingReimbursementTotal.value = payroll.grand_total || 0
    awaitingReimbursementCount.value = (payroll.submitters || []).reduce(
      (acc, b) => acc + (b.records ? b.records.length : 0),
      0,
    )

    arTotal.value = ar.grand_total || 0
    arCount.value = (ar.clients || []).reduce(
      (acc, c) => acc + (c.engagements || []).reduce((a, e) => a + (e.records ? e.records.length : 0), 0),
      0,
    )

    const monthRecords = Array.isArray(monthList) ? monthList : (monthList.results || [])
    monthCount.value = monthRecords.length
    monthTotal.value = sumAmounts(monthRecords)

    const pendingRecords = Array.isArray(pendingList) ? pendingList : (pendingList.results || [])
    pendingApprovalCount.value = pendingRecords.length
    pendingApprovalTotal.value = sumAmounts(pendingRecords)

    draftCount.value = draftPage.count != null ? draftPage.count : (Array.isArray(draftPage) ? draftPage.length : 0)
  } catch (e) {
    const tag = e && e._tag ? `[${e._tag}] ` : ''
    const detail = e && e.response && e.response.status ? `HTTP ${e.response.status}` : (e && e.message) || 'unknown error'
    errorMsg.value = `${tag}${detail}`
  } finally {
    loading.value = false
  }
}

function tagError(tag, err) {
  if (err && typeof err === 'object') err._tag = tag
  return err
}

load()
</script>
